import React, { useState, useEffect } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { useRBAC } from '../../hooks/useRBAC';
import { googleSheetsService } from '../../services/googleSheetsService';
import { financialPeriodService } from '../../services/financialPeriodService';
import './GoogleSheetsImport.css';

const GoogleSheetsImport = () => {
  const { showSuccess, showError } = useNotifications();
  const { isAdmin } = useRBAC();
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [financialPeriods, setFinancialPeriods] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    sheet_type: 'all',
    dry_run: false,
    force: false,
    financial_period: ''
  });

  // Load financial periods for invoicing imports
  useEffect(() => {
    const loadFinancialPeriods = async () => {
      try {
        const response = await financialPeriodService.getFinancialPeriods();
        if (response.success) {
          setFinancialPeriods(response.data || []);
        }
      } catch (error) {
        // Failed to load financial periods
      }
    };

    loadFinancialPeriods();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle preview import (dry_run=true)
  const handlePreviewImport = async () => {
    if (!isAdmin()) {
      showError('You do not have permission to perform this action');
      return;
    }

    setIsPreviewing(true);
    setImportResult(null);

    try {
      const result = await googleSheetsService.importFromSheets({ 
        sheet_type: formData.sheet_type,
        dry_run: true,
        force: false,
        financial_period: formData.financial_period || null
      });

      setImportResult(result);
      
      if (result.success) {
        showSuccess('Preview completed successfully');
      } else {
        showError(result.error || 'Preview failed');
      }
    } catch (error) {
      showError('Failed to preview import');
    } finally {
      setIsPreviewing(false);
    }
  };

  // Handle actual import (dry_run=false, force=false)
  const handleImport = async () => {
    if (!isAdmin()) {
      showError('You do not have permission to perform this action');
      return;
    }

    setIsLoading(true);
    setImportResult(null);

    try {
      const result = await googleSheetsService.importFromSheets({
        sheet_type: formData.sheet_type,
        dry_run: false,
        force: false,
        financial_period: formData.financial_period || null
      });

      setImportResult(result);
      
      if (result.success) {
        showSuccess('Import completed successfully');
      } else {
        showError(result.error || 'Import failed');
      }
    } catch (error) {
      showError('Failed to import data');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle force import (dry_run=false, force=true)
  const handleForceImport = async () => {
    if (!isAdmin()) {
      showError('You do not have permission to perform this action');
      return;
    }

    setIsLoading(true);
    setImportResult(null);

    try {
      const result = await googleSheetsService.importFromSheets({
        sheet_type: formData.sheet_type,
        dry_run: false,
        force: true,
        financial_period: formData.financial_period || null
      });

      setImportResult(result);
      
      if (result.success) {
        showSuccess('Force import completed successfully');
      } else {
        showError(result.error || 'Force import failed');
      }
    } catch (error) {
      showError('Failed to force import data');
    } finally {
      setIsLoading(false);
    }
  };

  // Sheet type options
  const sheetTypeOptions = [
    { value: 'all', label: 'All Sheets', description: 'Import from all supported sheets' },
    { value: 'membership', label: 'Membership', description: 'Import member organization data' },
    { value: 'invoicing', label: 'Invoicing', description: 'Import invoice and payment data' },
    { value: 'financial-periods', label: 'Financial Periods', description: 'Import financial period definitions' },
    { value: 'distributors', label: 'Distributors', description: 'Import distributor information' }
  ];

  // Render import result
  const renderImportResult = () => {
    if (!importResult) return null;

    const { 
      success, 
      operation, 
      sheet_type, 
      dry_run, 
      force, 
      message, 
      imported, 
      updated, 
      skipped, 
      errors, 
      total_rows,
      details 
    } = importResult;

    const isPreview = dry_run === true;
    const isForce = force === true;

    return (
      <div className={`import-result ${success ? 'success' : 'error'}`}>
        <div className="result-header">
          <h3>
            {isPreview ? '👁️ Preview Results' : 
             isForce ? '⚡ Force Import Results' : 
             success ? '✅ Import Successful' : '❌ Import Failed'}
          </h3>
          <p className="result-message">{message}</p>
          
          <div className="result-meta">
            <span className="meta-item">
              <strong>Operation:</strong> {operation || 'import'}
            </span>
            <span className="meta-item">
              <strong>Sheet Type:</strong> {sheet_type || 'all'}
            </span>
            <span className="meta-item">
              <strong>Mode:</strong> {isPreview ? 'Preview (Dry Run)' : isForce ? 'Force Import' : 'Normal Import'}
            </span>
            {total_rows && (
              <span className="meta-item">
                <strong>Total Rows:</strong> {total_rows}
              </span>
            )}
          </div>
        </div>

        {success && (
          <div className="result-stats">
            <div className="stat-item">
              <span className="stat-label">Imported:</span>
              <span className="stat-value imported">{imported || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Updated:</span>
              <span className="stat-value updated">{updated || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Skipped:</span>
              <span className="stat-value skipped">{skipped || 0}</span>
            </div>
            {total_rows && (
              <div className="stat-item">
                <span className="stat-label">Total Rows:</span>
                <span className="stat-value total">{total_rows}</span>
              </div>
            )}
          </div>
        )}

        {errors && errors.length > 0 && (
          <div className="result-errors">
            <h4>Errors ({errors.length}):</h4>
            <ul>
              {errors.map((error, index) => (
                <li key={index} className="error-item">{error}</li>
              ))}
            </ul>
          </div>
        )}

        {details && (
          <div className="result-details">
            <h4>Detailed Results by Sheet Type:</h4>
            <div className="details-grid">
              {Object.entries(details).map(([sheetType, data]) => (
                <div key={sheetType} className="detail-item">
                  <h5>{sheetType.charAt(0).toUpperCase() + sheetType.slice(1).replace('-', ' ')}</h5>
                  <div className="detail-stats">
                    <div className="detail-stat">
                      <span className="detail-label">Imported:</span>
                      <span className="detail-value imported">{data.imported || 0}</span>
                    </div>
                    <div className="detail-stat">
                      <span className="detail-label">Updated:</span>
                      <span className="detail-value updated">{data.updated || 0}</span>
                    </div>
                    <div className="detail-stat">
                      <span className="detail-label">Skipped:</span>
                      <span className="detail-value skipped">{data.skipped || 0}</span>
                    </div>
                    {data.total_rows && (
                      <div className="detail-stat">
                        <span className="detail-label">Total Rows:</span>
                        <span className="detail-value total">{data.total_rows}</span>
                      </div>
                    )}
                  </div>
                  {data.errors && data.errors.length > 0 && (
                    <div className="detail-errors">
                      <strong>Errors:</strong>
                      <ul>
                        {data.errors.map((error, index) => (
                          <li key={index} className="detail-error">{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {isPreview && success && (
          <div className="preview-note">
            <p><strong>ℹ️ Preview Mode:</strong> No changes were made to the database. Use "Import Data" to perform the actual import.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="google-sheets-import">
      <div className="import-header">
        <h2>📥 Import from Google Sheets</h2>
        <p className="import-description">
          Import data from Google Sheets into the database. Use preview mode to test before importing.
        </p>
      </div>

      <div className="import-form-container">
        <div className="import-form">
          <div className="form-section">
            <h3>Import Configuration</h3>
            
            <div className="form-group">
              <label htmlFor="sheet_type" className="form-label">
                Sheet Type
              </label>
              <select
                id="sheet_type"
                name="sheet_type"
                value={formData.sheet_type}
                onChange={handleInputChange}
                className="form-select"
              >
                {sheetTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="form-help">
                {sheetTypeOptions.find(opt => opt.value === formData.sheet_type)?.description}
              </p>
            </div>

            {formData.sheet_type === 'invoicing' && (
              <div className="form-group">
                <label htmlFor="financial_period" className="form-label">
                  Financial Period (Optional)
                </label>
                <select
                  id="financial_period"
                  name="financial_period"
                  value={formData.financial_period}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">All Periods</option>
                  {financialPeriods.map(period => (
                    <option key={period.id} value={period.name}>
                      {period.name}
                    </option>
                  ))}
                </select>
                <p className="form-help">
                  Filter invoices by specific financial period
                </p>
              </div>
            )}

          </div>

          <div className="form-actions">
            <div className="action-group">
              <button
                type="button"
                onClick={handlePreviewImport}
                disabled={isPreviewing || isLoading}
                className="btn btn-outline"
                title="Preview import without making changes (dry_run=true)"
              >
                {isPreviewing ? '⏳ Previewing...' : '👁️ Preview Import'}
              </button>
              <p className="action-description">Preview changes without importing</p>
            </div>

            <div className="action-group">
              <button
                type="button"
                onClick={handleImport}
                disabled={isLoading || isPreviewing}
                className="btn btn-primary"
                title="Import data normally (dry_run=false, force=false)"
              >
                {isLoading ? '⏳ Importing...' : '📥 Import Data'}
              </button>
              <p className="action-description">Import data with error checking</p>
            </div>

            <div className="action-group">
              <button
                type="button"
                onClick={handleForceImport}
                disabled={isLoading || isPreviewing}
                className="btn btn-warning"
                title="Force import ignoring errors (dry_run=false, force=true)"
              >
                {isLoading ? '⏳ Force Importing...' : '⚡ Force Import'}
              </button>
              <p className="action-description">Import data ignoring errors</p>
            </div>
          </div>
        </div>

        {renderImportResult()}
      </div>

      <div className="import-info">
        <h3>ℹ️ Import Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <h4>Supported Sheet Types</h4>
            <ul>
              <li><strong>Membership:</strong> Member organization data</li>
              <li><strong>Invoicing:</strong> Invoice and payment information</li>
              <li><strong>Financial Periods:</strong> Financial period definitions</li>
              <li><strong>Distributors:</strong> Distributor information</li>
            </ul>
          </div>
          <div className="info-item">
            <h4>Best Practices</h4>
            <ul>
              <li>Always use Preview Mode first</li>
              <li>Import specific sheets when possible</li>
              <li>Check import results for errors</li>
              <li>Backup database before large imports</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleSheetsImport;